import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export function RedefinirSenhaInicialPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmar: '',
  });
  const [loading, setLoading] = useState(false);
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNova, setShowNova] = useState(false);

  const senhasIguais = form.novaSenha === form.confirmar;
  const senhaValida = form.novaSenha.length >= 8;
  const podeSalvar =
    form.senhaAtual.trim() &&
    senhaValida &&
    senhasIguais &&
    !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeSalvar) return;

    setLoading(true);
    try {
      await api.post('/auth/redefinir-senha', {
        email: user?.email,
        senhaAtual: form.senhaAtual,
        novaSenha: form.novaSenha,
      });

      toast.success('Senha definida com sucesso. Faça login novamente.');
      logout();
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Erro ao redefinir senha';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 shadow-md">
            <KeyRound className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Defina sua senha</h1>
          <p className="mt-1 text-sm text-slate-500">
            Você está usando uma senha temporária. Crie uma senha pessoal para continuar.
          </p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          {user && (
            <div className="mb-5 flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                {user.nome.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{user.nome}</p>
                <p className="truncate text-xs text-slate-400">{user.email}</p>
              </div>
              <ShieldCheck className="ml-auto h-4 w-4 shrink-0 text-emerald-500" />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Senha atual */}
            <div className="space-y-1.5">
              <Label htmlFor="senhaAtual">Senha temporária</Label>
              <div className="relative">
                <Input
                  id="senhaAtual"
                  type={showSenhaAtual ? 'text' : 'password'}
                  value={form.senhaAtual}
                  onChange={(e) => setForm((f) => ({ ...f, senhaAtual: e.target.value }))}
                  placeholder="Senha que você recebeu"
                  className="pr-10"
                  autoFocus
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowSenhaAtual((v) => !v)}
                >
                  {showSenhaAtual ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Nova senha */}
            <div className="space-y-1.5">
              <Label htmlFor="novaSenha">Nova senha</Label>
              <div className="relative">
                <Input
                  id="novaSenha"
                  type={showNova ? 'text' : 'password'}
                  value={form.novaSenha}
                  onChange={(e) => setForm((f) => ({ ...f, novaSenha: e.target.value }))}
                  placeholder="Mínimo 8 caracteres"
                  className={cn(
                    'pr-10',
                    form.novaSenha && !senhaValida && 'border-red-300 focus-visible:ring-red-200',
                  )}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowNova((v) => !v)}
                >
                  {showNova ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.novaSenha && !senhaValida && (
                <p className="text-xs text-red-500">Mínimo 8 caracteres</p>
              )}
            </div>

            {/* Confirmar */}
            <div className="space-y-1.5">
              <Label htmlFor="confirmar">Confirmar nova senha</Label>
              <Input
                id="confirmar"
                type="password"
                value={form.confirmar}
                onChange={(e) => setForm((f) => ({ ...f, confirmar: e.target.value }))}
                placeholder="Repita a nova senha"
                className={cn(
                  form.confirmar && !senhasIguais && 'border-red-300 focus-visible:ring-red-200',
                )}
              />
              {form.confirmar && !senhasIguais && (
                <p className="text-xs text-red-500">As senhas não coincidem</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={!podeSalvar}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Definir senha e entrar
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-slate-400">
          Após salvar, você será redirecionado para o login.
        </p>
      </div>
    </div>
  );
}
